import { isAppleMusicAvailable, lookupAppleMusicSong, searchAppleMusic } from './appleMusicApi';
import { lookupItunesTrack, searchItunes, type SongSearchResult } from './itunesApi';
import type { SearchCatalog } from './playModes';

export { formatDuration, isExplicit } from './itunesApi';
export type { SongSearchResult } from './itunesApi';

const CACHE_TTL_MS = 5 * 60 * 1000;
const FALLBACK_CACHE_TTL_MS = 60 * 1000;

const searchCache = new Map<string, { results: SongSearchResult[]; expiresAt: number }>();

export function clearSearchCache(): void {
  searchCache.clear();
}

function cacheResults(key: string, results: SongSearchResult[], ttlMs: number): void {
  if (results.length === 0) return;
  searchCache.set(key, { results, expiresAt: Date.now() + ttlMs });
}

export async function searchSongs(
  query: string,
  limit = 10,
  mode: 'song' | 'artist' = 'song',
  catalog: SearchCatalog = 'apple_music',
): Promise<SongSearchResult[]> {
  if (!query || query.trim().length < 2) return [];

  const cacheKey = `${catalog}:${query.toLowerCase().trim()}:${limit}:${mode}`;
  const cached = searchCache.get(cacheKey);
  if (cached && Date.now() < cached.expiresAt) return cached.results;

  if (catalog === 'apple_music' && isAppleMusicAvailable()) {
    try {
      const results = await searchAppleMusic(query, limit, mode);
      cacheResults(cacheKey, results, CACHE_TTL_MS);
      return results;
    } catch (error) {
      if (isAppleMusicAvailable()) console.warn('[songSearch] Apple Music search failed, using iTunes', error);
    }
  }

  const results = await searchItunes(query, limit, mode);
  cacheResults(cacheKey, results, catalog === 'apple_music' ? FALLBACK_CACHE_TTL_MS : CACHE_TTL_MS);
  return results;
}

export async function getSongByTrackId(
  trackId: number | string,
  catalog: SearchCatalog = 'apple_music',
): Promise<SongSearchResult | null> {
  if (catalog === 'apple_music' && isAppleMusicAvailable()) {
    try {
      const song = await lookupAppleMusicSong(trackId);
      if (song) return song;
    } catch (error) {
      if (isAppleMusicAvailable()) console.warn('[songSearch] Apple Music lookup failed, using iTunes', error);
    }
  }
  return lookupItunesTrack(trackId);
}
